import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from '../stores/auth';
import api from '../lib/api';
import { connectSocket, getSocket } from '../lib/socket';
import {
  ArrowLeft,
  MessageCircle,
  Plus,
  Search,
  X,
  MapPin,
  Route,
  RefreshCw,
  Trophy,
  User,
  Users,
} from 'lucide-react';

interface Participant {
  user_id: string;
  user_name: string;
  role: string;
}

interface Conversation {
  id: string;
  type: 'DIRECT' | 'GROUP';
  name: string | null;
  last_message: string | null;
  last_message_at: string | null;
  unread_count: number;
  participants: Participant[];
}

interface ChatUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-red-500',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function ConversationList() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const { data: conversations, isLoading } = useQuery<Conversation[]>({
    queryKey: ['conversations'],
    queryFn: () => api.get('/chat/conversations').then((res) => res.data.data),
  });

  const { data: chatUsers } = useQuery<ChatUser[]>({
    queryKey: ['chat-users'],
    queryFn: () => api.get('/chat/users').then((res) => res.data.data),
    enabled: showNewChat,
  });

  const createConversation = useMutation({
    mutationFn: (participantId: string) =>
      api
        .post('/chat/conversations', {
          type: 'DIRECT',
          participant_ids: [participantId],
        })
        .then((res) => res.data.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setShowNewChat(false);
      setLocation(`/chat/${data.id}`);
    },
  });

  // Connect socket and listen for new messages
  useEffect(() => {
    try {
      connectSocket();
    } catch {
      // No token yet
    }

    const socket = getSocket();
    if (!socket) return;

    const handleNewMessage = () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
    };

    socket.on('new_message_notification', handleNewMessage);

    return () => {
      socket.off('new_message_notification', handleNewMessage);
    };
  }, [queryClient]);

  const getConversationName = (conv: Conversation): string => {
    if (conv.type === 'GROUP' && conv.name) return conv.name;
    const other = conv.participants.find((p) => p.user_id !== user?.id);
    return other?.user_name || 'Chat';
  };

  const filteredConversations = conversations?.filter((conv) => {
    if (!searchQuery) return true;
    const name = getConversationName(conv).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const filteredUsers = chatUsers?.filter((u) => {
    if (u.id === user?.id) return false;
    if (!userSearch) return true;
    return (
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase())
    );
  });

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 pb-5">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Chat</h1>
            {conversations && (
              <p className="text-sm opacity-80">
                {conversations.filter((c) => c.unread_count > 0).length > 0
                  ? `${conversations.filter((c) => c.unread_count > 0).length} conversa(s) com novas mensagens`
                  : 'Suas conversas'}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowNewChat(true)}
            className="p-2 rounded-full bg-white/20 active:bg-white/30"
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
          <input
            type="text"
            placeholder="Buscar conversas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/20 text-white placeholder-white/60 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-white/60" />
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando conversas...</p>
            </div>
          </div>
        ) : !filteredConversations || filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <MessageCircle className="h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-1">
              {searchQuery ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa'}
            </h2>
            <p className="text-sm text-gray-500 text-center">
              {searchQuery
                ? 'Tente outro termo de busca'
                : 'Toque no + para iniciar uma conversa'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredConversations.map((conv) => {
              const name = getConversationName(conv);
              const avatarColor = getAvatarColor(name);

              return (
                <button
                  key={conv.id}
                  onClick={() => setLocation(`/chat/${conv.id}`)}
                  className="w-full flex items-center gap-3 p-4 text-left active:bg-gray-50 bg-white"
                >
                  {/* Avatar */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-full ${avatarColor} flex items-center justify-center`}
                  >
                    {conv.type === 'GROUP' ? (
                      <Users className="h-5 w-5 text-white" />
                    ) : (
                      <span className="text-sm font-bold text-white">
                        {getInitials(name)}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3
                        className={`text-sm truncate ${
                          conv.unread_count > 0
                            ? 'font-bold text-gray-900'
                            : 'font-medium text-gray-900'
                        }`}
                      >
                        {name}
                      </h3>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {formatTime(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p
                        className={`text-xs truncate ${
                          conv.unread_count > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'
                        }`}
                      >
                        {conv.last_message || 'Nenhuma mensagem'}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 ml-2 bg-indigo-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                          {conv.unread_count > 9 ? '9+' : conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-2">
        <div className="flex items-center justify-around">
          <button
            onClick={() => setLocation('/')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <MapPin className="h-6 w-6" />
            <span className="text-xs">Inicio</span>
          </button>
          <button
            onClick={() => setLocation('/route')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <Route className="h-6 w-6" />
            <span className="text-xs">Roteiro</span>
          </button>
          <button
            onClick={() => setLocation('/sync')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <RefreshCw className="h-6 w-6" />
            <span className="text-xs">Sync</span>
          </button>
          <button
            onClick={() => setLocation('/achievements')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <Trophy className="h-6 w-6" />
            <span className="text-xs">Ranking</span>
          </button>
          <button
            onClick={() => setLocation('/profile')}
            className="flex flex-col items-center gap-1 p-2 text-gray-500"
          >
            <User className="h-6 w-6" />
            <span className="text-xs">Perfil</span>
          </button>
        </div>
      </nav>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="w-full max-w-lg bg-white rounded-t-2xl max-h-[80vh] flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Nova Conversa</h2>
              <button
                onClick={() => {
                  setShowNewChat(false);
                  setUserSearch('');
                }}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Search Users */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-100 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
              </div>
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto">
              {!filteredUsers || filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  {chatUsers ? 'Nenhum usuario encontrado' : 'Carregando usuarios...'}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredUsers.map((u) => {
                    const color = getAvatarColor(u.name);
                    return (
                      <button
                        key={u.id}
                        onClick={() => createConversation.mutate(u.id)}
                        disabled={createConversation.isPending}
                        className="w-full flex items-center gap-3 p-4 text-left active:bg-gray-50 disabled:opacity-50"
                      >
                        <div
                          className={`flex-shrink-0 w-10 h-10 rounded-full ${color} flex items-center justify-center`}
                        >
                          <span className="text-xs font-bold text-white">
                            {getInitials(u.name)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.role}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
